/**
 * Baseline Cache Service
 * 
 * Caches the 00:00 watt_hour baseline for each device in LocalStorage.
 * Since baseline is fixed for the day, we only need to fetch it once per day.
 * Cache automatically invalidates when the date changes.
 */

const CACHE_PREFIX = 'todayBaseline_';

interface BaselineCache {
  baseline: number;
  date: string;  // 'YYYY-MM-DD' format
  timestamp: number;
}

/**
 * Get today's date in 'YYYY-MM-DD' format (Taipei timezone)
 */
function getTodayDate(): string {
  const now = new Date();
  // Use Taipei timezone (UTC+8) - proper formatting without toISOString
  const taipeiOffset = 8 * 60; // +8:00 in minutes
  const localOffset = now.getTimezoneOffset(); // local offset in minutes (negative for east of UTC)
  const taipeiTime = new Date(now.getTime() + (taipeiOffset + localOffset) * 60 * 1000);
  const year = taipeiTime.getFullYear();
  const month = String(taipeiTime.getMonth() + 1).padStart(2, '0');
  const day = String(taipeiTime.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get cached baseline for a device
 * Returns null if cache doesn't exist or is from a different day
 */
export function getCachedBaseline(deviceId: string): number | null {
  try {
    const key = CACHE_PREFIX + deviceId;
    const cached = localStorage.getItem(key);
    
    if (!cached) {
      return null;
    }
    
    const data: BaselineCache = JSON.parse(cached);
    const today = getTodayDate();
    
    // Check if cache is from today
    if (data.date !== today) {
      // Cache expired (different day), remove it
      localStorage.removeItem(key);
      console.log(`📊 [BaselineCache] Cache expired for ${deviceId} (cached: ${data.date}, today: ${today})`);
      return null;
    }
    
    console.log(`📊 [BaselineCache] Cache hit for ${deviceId}: ${data.baseline.toFixed(3)} kWh`);
    return data.baseline;
  } catch (error) {
    console.error(`❌ [BaselineCache] Error reading cache for ${deviceId}:`, error);
    return null;
  }
}

/**
 * Save baseline to cache
 */
export function saveBaseline(deviceId: string, baseline: number): void {
  try {
    const key = CACHE_PREFIX + deviceId;
    const data: BaselineCache = {
      baseline,
      date: getTodayDate(),
      timestamp: Date.now()
    };
    
    localStorage.setItem(key, JSON.stringify(data));
    console.log(`📊 [BaselineCache] Saved baseline for ${deviceId}: ${baseline.toFixed(3)} kWh`);
  } catch (error) {
    console.error(`❌ [BaselineCache] Error saving cache for ${deviceId}:`, error);
  }
}

/**
 * Get cached baselines for multiple devices
 * Returns a Map of deviceId -> baseline (only includes devices with valid cache)
 */
export function getCachedBaselines(deviceIds: string[]): Map<string, number> {
  const result = new Map<string, number>();
  
  for (const deviceId of deviceIds) {
    const baseline = getCachedBaseline(deviceId);
    if (baseline !== null) {
      result.set(deviceId, baseline);
    }
  }
  
  return result;
}

/**
 * Check if all devices have cached baseline for today
 */
export function hasAllCachedBaselines(deviceIds: string[]): boolean {
  for (const deviceId of deviceIds) {
    if (getCachedBaseline(deviceId) === null) {
      return false;
    }
  }
  return deviceIds.length > 0;
}

/**
 * Clear all expired cache entries (entries not from today)
 */
export function clearExpiredCache(): void {
  try {
    const today = getTodayDate();
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        const cached = localStorage.getItem(key);
        if (cached) {
          try {
            const data: BaselineCache = JSON.parse(cached);
            if (data.date !== today) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid JSON, remove it
            keysToRemove.push(key);
          }
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (keysToRemove.length > 0) {
      console.log(`📊 [BaselineCache] Cleared ${keysToRemove.length} expired cache entries`);
    }
  } catch (error) {
    console.error('❌ [BaselineCache] Error clearing expired cache:', error);
  }
}

/**
 * Clear all baseline cache
 */
export function clearAllCache(): void {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`📊 [BaselineCache] Cleared all ${keysToRemove.length} cache entries`);
  } catch (error) {
    console.error('❌ [BaselineCache] Error clearing cache:', error);
  }
}

// ========== Today Energy Value Cache ==========
// 快取今日用電量的計算結果，頁面載入時立即顯示

const ENERGY_VALUE_KEY = 'todayEnergyValue';

interface EnergyValueCache {
  value: number;
  date: string;  // 'YYYY-MM-DD'
  timestamp: number;
  selectedMeters: string[];  // 用於驗證是否是同一組電錶
}

/**
 * Get cached today energy value
 * Returns null if cache doesn't exist, is from a different day, or meters changed
 */
export function getCachedTodayEnergy(selectedMeters: string[]): number | null {
  try {
    const cached = localStorage.getItem(ENERGY_VALUE_KEY);
    if (!cached) return null;
    
    const data: EnergyValueCache = JSON.parse(cached);
    const today = getTodayDate();
    
    // Check if cache is from today
    if (data.date !== today) {
      localStorage.removeItem(ENERGY_VALUE_KEY);
      console.log(`📊 [EnergyCache] Cache expired (different day)`);
      return null;
    }
    
    // Check if same meters are selected
    const cachedMetersKey = [...data.selectedMeters].sort().join(',');
    const currentMetersKey = [...selectedMeters].sort().join(',');
    if (cachedMetersKey !== currentMetersKey) {
      console.log(`📊 [EnergyCache] Cache invalid (meters changed)`);
      return null;
    }
    
    console.log(`📊 [EnergyCache] Cache hit: ${data.value.toFixed(3)} kWh (cached ${Math.round((Date.now() - data.timestamp) / 1000)}s ago)`);
    return data.value;
  } catch (error) {
    console.error('❌ [EnergyCache] Error reading cache:', error);
    return null;
  }
}

/**
 * Save today energy value to cache
 */
export function saveTodayEnergy(value: number, selectedMeters: string[]): void {
  try {
    const data: EnergyValueCache = {
      value,
      date: getTodayDate(),
      timestamp: Date.now(),
      selectedMeters: [...selectedMeters]
    };
    
    localStorage.setItem(ENERGY_VALUE_KEY, JSON.stringify(data));
    console.log(`📊 [EnergyCache] Saved: ${value.toFixed(3)} kWh`);
  } catch (error) {
    console.error('❌ [EnergyCache] Error saving cache:', error);
  }
}
